import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Input,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import {
  getResaleInventoryApi,
  deleteResaleInventoryApi,
  updateResaleInventoryStatusApi,
} from "../../apiServices/resaleInventoryServices";
import ResaleInventoryModal from "./ResaleInventoryModal";
import ResaleInventoryDetailsModal from "./ResaleInventoryDetailsModal";
import DeleteModal from "../../components/Common/DeleteModal";
import { formatIndianNumber } from "../../utils/indianNumberFormatter";

const SALE_STATUSES = [
  "Available",
  "Under Negotiation",
  "Token Received",
  "Sold",
  "On Hold",
];

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Available":
      return "success";
    case "Under Negotiation":
      return "warning";
    case "Token Received":
      return "info";
    case "Sold":
      return "danger";
    case "On Hold":
      return "secondary";
    default:
      return "primary";
  }
};

const ResaleInventoryList = () => {
  document.title = "Resale Properties | RS-CRM";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await deleteResaleInventoryApi(deleteId);
      toast.success("Resale property deleted successfully");
      setDeleteModalOpen(false);
      setDeleteId(null);
      fetchInventory();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete resale property");
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        search,
        status: selectedStatus,
      };
      const res = await getResaleInventoryApi(params);
      const data = res?.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch resale inventory");
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedStatus]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setPage(1);
  };

  const handleAddClick = () => {
    setEditItem(null);
    setModalOpen(true);
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setModalOpen(true);
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setDetailsModalOpen(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateResaleInventoryStatusApi(id, newStatus);
      toast.success(`Sale status updated to '${newStatus}'`);
      fetchInventory();
      if (selectedItem && selectedItem._id === id) {
        setSelectedItem((prev) => ({ ...prev, sale_status: newStatus }));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  const handleLogAdded = (updatedItem) => {
    fetchInventory();
    if (updatedItem) {
      setSelectedItem(updatedItem);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="RS CRM" breadcrumbItem="Resale Properties" />

          <Row className="mb-3 align-items-center">
            <Col md={6}>
              <h4 className="card-title mb-0">Resale Property Inventory</h4>
              <p className="text-muted mb-0 font-size-13">
                Total {total} resale property listings found
              </p>
            </Col>
            <Col md={6} className="text-end mt-2 mt-md-0">
              <Button
                color="soft-secondary"
                className="me-2"
                onClick={fetchInventory}
                disabled={loading}
                title="Refresh Listings"
              >
                <i className={`mdi mdi-refresh me-1 ${loading ? "mdi-spin" : ""}`}></i> Refresh
              </Button>
              <Button color="primary" onClick={handleAddClick}>
                <i className="mdi mdi-plus me-1"></i> Add Resale Listing
              </Button>
            </Col>
          </Row>

          {/* Status Tabs & Search Filter */}
          <Card className="mb-3">
            <CardBody className="p-2">
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    color={selectedStatus === "" ? "primary" : "soft-secondary"}
                    onClick={() => handleStatusFilter("")}
                  >
                    All Properties ({total})
                  </Button>
                  {SALE_STATUSES.map((st) => (
                    <Button
                      key={st}
                      size="sm"
                      color={selectedStatus === st ? "primary" : "soft-secondary"}
                      onClick={() => handleStatusFilter(st)}
                    >
                      {st}
                    </Button>
                  ))}
                </div>

                <div style={{ minWidth: "250px" }} className="w-100 w-md-auto">
                  <Input
                    type="text"
                    placeholder="Search by name, contact, type, sector..."
                    value={search}
                    onChange={handleSearchChange}
                    className="form-control-sm"
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Desktop Table View (Visible >= 768px) */}
          <Card className="d-none d-md-block">
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table hover responsive className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: "220px" }}>Property & Owner</th>
                      <th style={{ minWidth: "180px" }}>Location & Specs</th>
                      <th style={{ minWidth: "130px" }}>Sell Price</th>
                      <th style={{ minWidth: "140px" }}>Sale Status</th>
                      <th style={{ width: "130px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5">
                          <Spinner color="primary" />
                          <div className="text-muted mt-2">Loading resale inventories...</div>
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">
                          <i className="mdi mdi-home-city-outline font-size-24 d-block mb-2 text-secondary"></i>
                          No resale property inventory records found.
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => {
                        const details = item.property_details || {};
                        const location = details.location || {};
                        return (
                          <tr
                            key={item._id}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleViewDetails(item)}
                            title="Click row to view property details & activity logs"
                          >
                            <td>
                              <div className="fw-bold text-primary font-size-14 mb-1">
                                <i className="mdi mdi-home-city me-1"></i>
                                {item.name}
                              </div>
                              <div className="d-flex align-items-center gap-1 font-size-12">
                                <Badge color="primary">{details.type || "Property"}</Badge>
                                {details.bhk && <Badge color="info">{details.bhk}</Badge>}
                                <span className="text-muted ms-1">
                                  <i className="mdi mdi-phone me-1 text-success"></i>
                                  {item.contact_number}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="font-size-13 fw-semibold">
                                {location.sector || location.city
                                  ? `${location.sector || ""}, ${location.city || ""}`
                                  : location.address || "N/A"}
                              </div>
                              <div className="font-size-12 text-muted">
                                {details.size_sqft ? `${details.size_sqft} Sq. Ft.` : ""}
                                {location.googleMapLink && (
                                  <a
                                    href={location.googleMapLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary ms-2"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className="mdi mdi-map-marker"></i> Map
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="fw-bold text-success font-size-14">
                              {formatIndianNumber(item.sell_price)}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <UncontrolledDropdown>
                                <DropdownToggle
                                  tag="button"
                                  className={`btn btn-soft-${getStatusBadgeClass(
                                    item.sale_status
                                  )} btn-sm dropdown-toggle`}
                                >
                                  {item.sale_status} <i className="mdi mdi-chevron-down ms-1"></i>
                                </DropdownToggle>
                                <DropdownMenu>
                                  {SALE_STATUSES.map((st) => (
                                    <DropdownItem
                                      key={st}
                                      onClick={() => handleStatusChange(item._id, st)}
                                    >
                                      {st}
                                    </DropdownItem>
                                  ))}
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                            <td className="text-end" onClick={(e) => e.stopPropagation()}>
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  color="soft-primary"
                                  className="btn-sm px-2 py-1"
                                  title="View Details"
                                  onClick={() => handleViewDetails(item)}
                                >
                                  <i className="mdi mdi-eye font-size-18"></i>
                                </Button>
                                <Button
                                  color="soft-info"
                                  className="btn-sm px-2 py-1"
                                  title="Edit Listing"
                                  onClick={() => handleEditClick(item)}
                                >
                                  <i className="mdi mdi-pencil font-size-18"></i>
                                </Button>
                                <Button
                                  color="soft-danger"
                                  className="btn-sm px-2 py-1"
                                  title="Delete Listing"
                                  onClick={() => handleDeleteClick(item._id)}
                                >
                                  <i className="mdi mdi-trash-can font-size-18"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted font-size-13">
                    Showing page {page} of {totalPages} ({total} items)
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      color="secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      color="secondary"
                      disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Mobile Card View (Visible < 768px) */}
          <div className="d-block d-md-none mb-3">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="text-muted mt-2">Loading resale inventories...</div>
              </div>
            ) : items.length === 0 ? (
              <Card className="text-center py-5 text-muted">
                <CardBody>
                  <i className="mdi mdi-home-city-outline font-size-24 d-block mb-2 text-secondary"></i>
                  No resale property inventory records found.
                </CardBody>
              </Card>
            ) : (
              items.map((item) => {
                const details = item.property_details || {};
                const location = details.location || {};
                return (
                  <Card
                    key={item._id}
                    className="mb-3 border shadow-sm"
                    style={{ borderRadius: "10px" }}
                  >
                    <CardBody className="p-3">
                      {/* Top Header: Owner Name + Price */}
                      <div
                        className="d-flex justify-content-between align-items-start mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewDetails(item)}
                      >
                        <div>
                          <h6 className="fw-bold text-primary mb-1 font-size-15">
                            <i className="mdi mdi-home-city me-1"></i> {item.name}
                          </h6>
                          <div className="text-muted font-size-12">
                            <i className="mdi mdi-phone me-1 text-success"></i>
                            <a href={`tel:${item.contact_number}`} className="text-reset" onClick={(e) => e.stopPropagation()}>
                              {item.contact_number}
                            </a>
                          </div>
                        </div>
                        <div className="text-end">
                          <span className="fw-bold text-success font-size-15 d-block">
                            {formatIndianNumber(item.sell_price)}
                          </span>
                          <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
                            <DropdownToggle
                              tag="button"
                              className={`btn btn-soft-${getStatusBadgeClass(
                                item.sale_status
                              )} btn-sm dropdown-toggle mt-1 py-0 px-2`}
                            >
                              {item.sale_status} <i className="mdi mdi-chevron-down ms-1"></i>
                            </DropdownToggle>
                            <DropdownMenu>
                              {SALE_STATUSES.map((st) => (
                                <DropdownItem
                                  key={st}
                                  onClick={() => handleStatusChange(item._id, st)}
                                >
                                  {st}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </div>
                      </div>

                      {/* Property Specs Badges */}
                      <div
                        className="d-flex flex-wrap gap-1 align-items-center mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewDetails(item)}
                      >
                        <Badge color="soft-primary">{details.type || "Property"}</Badge>
                        {details.bhk && <Badge color="soft-info">{details.bhk}</Badge>}
                        {details.size_sqft && (
                          <span className="font-size-12 text-muted ms-1">{details.size_sqft} Sq. Ft.</span>
                        )}
                      </div>

                      {/* Location */}
                      <div
                        className="font-size-12 text-muted mb-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewDetails(item)}
                      >
                        <i className="mdi mdi-map-marker me-1 text-danger"></i>
                        {location.sector || location.city
                          ? `${location.sector || ""}, ${location.city || ""}`
                          : location.address || "N/A"}
                        {location.googleMapLink && (
                          <a
                            href={location.googleMapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary ms-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            (Maps)
                          </a>
                        )}
                      </div>

                      {/* Card Footer: Metadata & Actions */}
                      <div className="d-flex justify-content-between align-items-center border-top pt-2">
                        <small className="text-muted font-size-11">
                          By {item.createdBy?.name || "System"} • {new Date(item.createdAt).toLocaleDateString("en-IN")}
                        </small>
                        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            color="soft-primary"
                            className="btn-sm px-2 py-1"
                            title="View Details"
                            onClick={() => handleViewDetails(item)}
                          >
                            <i className="mdi mdi-eye font-size-18 me-1"></i> View
                          </Button>
                          <Button
                            color="soft-info"
                            className="btn-sm px-2 py-1"
                            title="Edit Listing"
                            onClick={() => handleEditClick(item)}
                          >
                            <i className="mdi mdi-pencil font-size-18 me-1"></i> Edit
                          </Button>
                          <Button
                            color="soft-danger"
                            className="btn-sm px-2 py-1"
                            title="Delete Listing"
                            onClick={() => handleDeleteClick(item._id)}
                          >
                            <i className="mdi mdi-trash-can font-size-18"></i>
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                );
              })
            )}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-2 bg-white rounded border">
                <div className="text-muted font-size-12">
                  Page {page} of {totalPages}
                </div>
                <div className="d-flex gap-1">
                  <Button
                    size="sm"
                    color="secondary"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    color="secondary"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      {/* Modals */}
      <ResaleInventoryModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        editData={editItem}
        onSuccess={fetchInventory}
      />

      <ResaleInventoryDetailsModal
        isOpen={detailsModalOpen}
        toggle={() => setDetailsModalOpen(!detailsModalOpen)}
        item={selectedItem}
        onLogAdded={handleLogAdded}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(!deleteModalOpen)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </React.Fragment>
  );
};

export default ResaleInventoryList;
